import React, { useEffect, useRef, useState } from "react";
import Column from "../../../../components/atoms/table/Column.atom";
import DataTable from "../../../../components/atoms/table/DataTable.atom";
import ProgressSpinner from "../../../../components/atoms/misc/ProgressSpinner.atom";
import useFetchTeamMemberVacationsAditionalTable from "./hooks/useFetchTeamMemberVacationsAditional.hook";
import { DateTime } from "luxon";
import ContextMenu from "../../../../components/atoms/ContextMenu.atom";
import InputNumber from "../../../../components/atoms/InputNumber.atom";
import InputText from "../../../../components/atoms/InputText.atom";
import IconWithTooltipAndCustomBody
  from "../../../../components/molecules/IconWithTooltipAndCustomBody.molecule";
import InputCalendarForm from "../../../../components/molecules/InputCalendarForm.molecule";
import { checkAllFields, fieldsHaveErrors, isNotEmpty, validateFieldValue, isANumberGreaterThanOrEqualZero } from "../../../../utils/validations.utils";
import { deleteAdditionalDaysPackage, updateAdditionalDaysPackage } from "../../../../services/vacation.service";
import useToast from "../../../../hooks/useToast.hook";
import { additionalDaysValidity } from "../../../../utils/vacation.utils";
import { ScreenMessage } from "../../../../components/compounds/customMessages/ScreenMessage.compound";
import { isDateAtWeekend } from "../../../../utils/date.utils";

export default function TeamMemberVacationsAditionalTable({
  teamMemberId,
  periodStartDate,
  periodEndDate,
  refresh = undefined,
  editable = false,
  triggerRefresh = () => {},
  workAnniversary
}) {
  const cmRef = useRef(null)
  const { setErrorMessage, setSuccessMessage } = useToast()
  const { additionalDays, isLoading } =
    useFetchTeamMemberVacationsAditionalTable({
      teamMemberId,
      periodStartDate: workAnniversary,
      periodEndDate,
      refresh
    });

  const [editableData, setEditableData] = useState(null)
  const [isEditing, setIsEditing] = useState(false)
  const [editableDataValidations, setEditableDataValidations] = useState({})

  useEffect(() => {
    if(editableData) {
      const validations = {
        availableDays: { value: editableData.availableDays, errors: [], typeValidations: [isANumberGreaterThanOrEqualZero] },
        motive: { value: editableData.motive, errors: [], typeValidations: [isNotEmpty] },
        expirationDate: { value: editableData.expirationDate, errors: [], typeValidations: [isNotEmpty] }
      }
      setEditableDataValidations(validations)
    } else {
      setEditableDataValidations({})
    }
  },[editableData])

  const cmOptions = [
  {
    label: "Editar",
    icon: "pi pi-pencil",
    command: () => {
      setIsEditing(true)
    }
  },
  {
    label: "Eliminar", 
    icon: "pi pi-trash",
    command: () => {
      handleDelete()
    }
  }
]




  const handleUpdate = async () => {
    checkAllFields(editableDataValidations, setEditableDataValidations)
    if(fieldsHaveErrors(editableDataValidations) || !editableData?.id) return
    try {
      const data = {
        id: editableData.id,
        availableDays: editableData.availableDays,
        motive: editableData.motive.trim(),
        expirationDate: editableData.expirationDate?.split('T')[0] || editableData.expirationDate
      }
      const response = await updateAdditionalDaysPackage(data)
       if(!response.success){
           return setErrorMessage({message: response.message || 'No se pudieron editar los días adicionales', lifeTime: 3000})
          }
      setSuccessMessage({message: 'Días adicionales editados con éxito', lifeTime: 3000})
      setIsEditing(false)
      setEditableData(null)
      triggerRefresh()
    } catch (error) {
      setErrorMessage({message: error.message || 'No se pudieron editar los días adicionales', lifeTime: 3000})
    }
  }

  const handleDelete = async () => {
    if(!editableData?.id) return
    try {
      const response = await deleteAdditionalDaysPackage(editableData.id)
       if(!response.success){
           return setErrorMessage({message: response.message || 'No se pudieron eliminar los días adicionales', lifeTime: 3000})
          }
      setSuccessMessage({message: 'Días adicionales eliminados con éxito', lifeTime: 3000})
      setIsEditing(false)
      setEditableData(null)
      triggerRefresh()
    } catch (error) {
      setErrorMessage({message: error.message || 'No se pudieron eliminar los días adicionales', lifeTime: 3000})
    }
  }

  const handleEditableDataChange = (name, value) => {
    let processedValue = value
    if (name === 'expirationDate' && value && value.isValid) {
      processedValue = value.toISO()
    }
    
    setEditableData(prev => ({ ...prev, [name]: processedValue }))
    setEditableDataValidations(validateFieldValue(editableDataValidations, name, processedValue))
  }


  if (isLoading)
    return (
      <div className="flex justify-content-center p-5">
        <ProgressSpinner />
      </div>
    );

const formatDate = (value) => {
  if (!value) return "";

  if (value instanceof Date) {
    const dt = DateTime.fromJSDate(value);
    return dt.isValid ? dt.toFormat("dd/LL/yyyy") : "";
  }
  if (typeof value === "string") {
    const onlyDate = value.split("T")[0]; 
    const dt = DateTime.fromISO(onlyDate); 
    return dt.isValid ? dt.toFormat("dd/LL/yyyy") : "";
  }
  return "";
};

const isEditableField = (rowId) => {
  return isEditing && editableData?.id === rowId
}

  return (
    <>
    <DataTable 
    value={additionalDays} 
    className={editable ? (isEditing ? "aditional-days-table-editable--edition-mode" : "aditional-days-table-editable") : "aditional-days-table"}
    scrollable 
    rowClassName={(row) => {
    return (row.status === "Vencidos" || row.status === "Tomados")
      ? "row-disabled"
      : "";
    }}
    emptyCustomMessage={<EmptyAdditionalDaysMessage />}
    >

      <Column header="Cantidad" body={(row) => {
        if(isEditableField(row.id)) {
           return (
            <div className="editable-quantity-inputnumber-container">
              <InputNumber
               showButtons
               step={1}
               value={editableData.availableDays}
               min={row.takenDays || 0}
               onChange={(e) => handleEditableDataChange('availableDays', e.value)}
             />
            </div>
           )
        } else {
          return (row.availableDays || 0) + (row.takenDays || 0)
        }
      }} />

      {!isEditing && <Column header="Solicitados" body={(row) => row.takenDays} />}

      <Column header="Motivo" field="motive" body={(row) => {
        if(isEditableField(row.id)) {
            return (
              <InputText
                value={editableData.motive}
                placeholder='Motivo'
                onChange={(e) => handleEditableDataChange('motive', e.target.value)}
                />
            )
        } else {
            return row.motive
        }
      }}/>

      {!isEditing && <Column header="Estado" field="status" />}

      <Column
      header={
        <div className="flex align-items-center gap-2">
          <span>Vigencia</span>
          <IconWithTooltipAndCustomBody
            tooltipBody="La vigencia indica el periodo en el que se pueden tomar los días adicionales."
            position="right"
            iconClassName="pi pi-info-circle mx-1"
          />
        </div>
        }
        body={(row) => {
          if(isEditableField(row.id)) {
            const dateValue = editableData.expirationDate 
              ? DateTime.fromISO(editableData.expirationDate.split('T')[0])
              : null
            
            return (
              <InputCalendarForm
              placeholder='Vigencia'
              value={dateValue}
              onChange={(date) => handleEditableDataChange('expirationDate', date)}
              minDate={additionalDaysValidity(periodStartDate).minDate}
              maxDate={additionalDaysValidity(periodStartDate).maxDate}
              disabledDate={(current) => isDateAtWeekend(current)}
              />
            )
          } else {
            return formatDate(row.expirationDate)
          }
        }}
      />
      {
        editable && (
          <Column
          body={(row) => {
            if(row.status === "Vencidos" || row.status === "Tomados") return null
            if (isEditableField(row.id)) {
              return (
                <div className="flex gap-4">
                  <i className="pi pi-check cursor-pointer" onClick={() => {
                    handleUpdate()
                  }}/>
                  <i className="pi pi-times cursor-pointer" onClick={() => {
                    setIsEditing(false)
                    setEditableData(null)
                  }}/>
                </div>
              )
            }
            
            return <i 
                     className={`pi pi-ellipsis-v ${isEditing ? "" : "cursor-pointer"}`}
                     style={isEditing ? { opacity: 0.4 } : {}} 
                     onClick={(event) => {
                       if(isEditing) return
                       setIsEditing(false)
                       setEditableData(row)
                       cmRef?.current?.show(event)
                     }}
                   />
          }}
          className="justify-content-end"
          />
        )
      }

    </DataTable>
    <ContextMenu 
      model={cmOptions} 
      ref={cmRef} 
      className="additional-days-context-menu"
    />
    </>
  );
}

export function EmptyAdditionalDaysMessage() {
  return (
    <div className="empty-message-center mx-auto">
      <ScreenMessage
        className="mx-auto py-2"
        icon={<i className="pi pi-folder-open" />}
        title="Sin días adicionales"
        message="Sé buena onda y dale un día libre"
      />
    </div>
  )
}